import Link from 'next/link'
import React from 'react'

import ExternalLink from '../components/ExternalLink'
import LegalPage from '../components/LegalPage'
import { SUPPORT_EMAIL } from '../config'
import { PRIVACY_POLICY } from '../constants/Routes'

const TermsView = () => {
  return (
    <LegalPage title='Terms of Service'>
      <p>
        PLEASE READ THESE TERMS OF SERVICE CAREFULLY. BY ACCESSING OR USING THE PLATFORM, AS DEFINED BELOW, YOU AGREE TO
        BE LEGALLY BOUND BY THESE TERMS OF SERVICE AND ALL TERMS, POLICIES, AND AGREEMENTS INCORPORATED BY REFERENCE. IF
        YOU DO NOT AGREE WITH THESE TERMS PLEASE DO NOT ACCESS OR USE THE PLATFORM. WE RESERVE THE RIGHT AT OUR SOLE
        DISCRETION, TO CHANGE, MODIFY, ADD OR REMOVE PORTIONS OF THESE TERMS OF SERVICE, AT ANY TIME.
        <br />
        <br />
        YOU SHOULD BE AWARE THAT THE RISK OF LOSS IN PURCHASING OR HOLDING CRYPTO ASSETS OR NON-FUNGIBLE TOKENS CAN BE
        SUBSTANTIAL. WE DO NOT ACT AS YOUR BROKER-DEALER, INTERMEDIARY, AGENT, OR ANY KIND OF ADVISOR, INCLUDING
        COMMODITY TRADING ADVISOR. YOU SHOULD THEREFORE CAREFULLY CONSIDER WHETHER PURCHASING OR HOLDING CRYPTO ASSETS
        OR NON-FUNGIBLE TOKENS ARE SUITABLE FOR YOU IN LIGHT OF YOUR FINANCIAL CIRCUMSTANCES.
      </p>
      <h3>1. Acceptance of these Terms</h3>
      <ol type='a'>
        <li>
          These Terms of Service (“Terms”) is a legal agreement between you and PITCCH LTD, a limited company
          incorporated in the United Kingdom and whose registered address is 71-75 Shelton Street, Covent Garden,
          London, WC2H 9JQ, UK. References in these Terms to “Triptcip”, “we”, “our” or “us”, are to PITCCH LTD, and
          references to “you” or “your” are to the person with whom Triptcip enters into these Terms.
        </li>
        <li>
          Depending on your usage of the Platform we may refer to you: i) as a “Creator” when you issue NFT tokens (as
          defined below) that represent your original audio works (“Work”) and/or sell NFT tokens via the Platform, or
          ii) as a “Collector” when you purchase any NFT tokens via the Platform, and/or make bids on auctions via the
          Platform to purchase such.
        </li>
        <li>
          These Terms apply to your use and access to the Platform and related services. Upon accessing or signing in at
          the Platform you agree to be legally bound by and to comply with these Terms and all additional terms,
          policies, and agreements incorporated herein by reference and amended from time to time in our sole
          discretion.
        </li>
        <li>
          “NFT token” means a non-fungible token (NFT), that is a special type of crypto assets on blockchain with
          unique identification codes and metadata that distinguish them from each other. All the NFT tokens are created
          (minted) by you (when you act as the Creator) through the smart contract that is the part of the Platform, NFT
          tokens represent your Work as well as rights in this Work. In these Terms we may refer to NFT tokens
          representing original audio works as “Digital Artworks”, and to the Work that is represented by the NFT token
          as “Related Work”.
        </li>
      </ol>
      <h3>2. Platform</h3>
      <ol type='a'>
        <li>
          We operate the platform accessible via mobile applications and the website
          {' '}
          <ExternalLink href='https://triptip.com'>
            https://triptcip.com
          </ExternalLink>
          {' '}
          (the “Platform”). The Platform is a
          peer-to-peer marketplace that provides the functionality of connecting users for buying and selling of Digital
          Artworks, creation (minting) of Digital Artworks related to specific audio works as well as listening or
          previewing available audio works.
        </li>
        <li>
          The Platform is offered on a self-service basis in accordance with the terms and conditions of these Terms.
          You are responsible for ensuring that you are accessing the Platform via secure and trustworthy sources or
          devices.
        </li>
        <li>
          We do not and will not keep custody over Digital Artworks and your funds, we do not and will not store, keep
          or control Digital Artworks and your funds. We do not buy from you or sell to you any Digital Artworks. Our
          smart contracts do not give us custody, possession, or control of any Digital Artwork and your funds. The
          actual contract for sale of the Digital Artwork is made directly between the Creator and the Collector. We are
          not a party to the transaction, and we are not and shall not be considered as a seller or an auctioneer. Also
          we are not an issuer or the owner of any Digital Artwork.
        </li>
        <li>
          To access certain functionality (including, but not limited to, engaging in any transaction at the Platform,
          listing, bidding or buying of any Digital Artwork) you would need to sign in by connecting MetaMask wallet to
          the Platform. In order to sign in, you would need to have a wallet with the Ethereum blockchain connected to
          MetaMask gateway <ExternalLink href='https://metamask.io'>https://metamask.io</ExternalLink>. The wallet at
          the Ethereum blockchain can be created either via Ethereum directly or via decentralised application, wallet,
          gateway supporting the Ethereum blockchain. We do not support the functionality of the creation of Ethereum
          wallets or connecting Ethereum wallets to decentralised applications, wallets, or protocols.
        </li>
        <li>
          It is solely your responsibility to keep safe and guard your password, seed phrases and/or private key related
          to your wallets. We assume no responsibility or liability for lost or misplaced passwords, seed phrases,
          personal information, or private keys. You are fully responsible for each and every transaction made under
          your wallet connected to the Platform, including, but not limited to, payment of all fees related to your use
          of our services. We shall never be liable for any loss or damage due to the lack of security of your connected
          wallet.
        </li>
        <li>
          We reserve the right to allow access to the Platform or its certain functionality, or to list any Digital
          Artworks or to create any NFT tokens via the Platform only to users specifically invited by us and/or by our
          existing users.
        </li>
        <li>
          Any your transactions via the Platform will be initiated by you and will be executed by the corresponding
          smart contract on your behalf. More specifically, smart contract would facilitate your bids, listings,
          execution of sales, creation of NFT tokens, transfer of payments, and purchased NFT tokens. By making any
          transaction via the Platform you irrevocably agree to be bound by any outcome of smart contract operations and
          its further execution of your transaction (for example, execution of sale contract when your bid wins an
          auction).
        </li>
        <li>
          You hereby acknowledge and agree that smart contracts, blockchains, and distribute ledgers are still new
          technologies and are still experimental in nature. We do not represent and warrant that any smart contract and
          blockchain would operate as expected, that execution of any transaction would be uninterrupted, accurate, and
          timely.
        </li>
      </ol>
      <h3>3. Initial Sale</h3>
      <ol type='a'>
        <h4>Listing the work</h4>
        <li>
          When the Creator creates the listing of Digital Artwork he/she intends to sell (“Listing”), the Creator
          directs the related smart contract to create the specific NFT token on the blockchain (for example, Ethereum
          blockchain) that would correspond to this specific audio work and would be the proof of ownership of this
          audio work. In further this NFT token would be transferred by the Creator via a smart contract as a part of
          the initiated transaction to the Collector as well as to any further Collectors of the audio work. Any history
          of transfers and changes of ownership of the NFT token is visible to the public at the corresponding
          blockchain.
        </li>
        <li>
          To create the Listing, the Creator shall upload the Work to the Platform, provide required information about
          the Work and its description, provide graphical artwork (cover) for the Work, provide any preview or sample
          materials, provide sale conditions (for example, auction price conditions, crypto asset for payment, the price
          for instant purchase, conditions for the further re-sale of the Digital Artwork by the Collector), as well as
          initiate a request to the smart contract for NFT token creation.
        </li>
        <li>
          By creating the Listing the Creator represents and warrants the following:
          <ol type='i'>
            <li>
              The Work is an original creation of the Creator or was purchased from the initial creator through the
              Platform;
            </li>
            <li>
              The Creator possesses the full scope of intellectual property rights in the Work, including, but not
              limited to, the right to sell the Work, the right to assign intellectual property rights in the Work, the
              right to distribute the Work, the right to make the Work available to the public;
            </li>
            <li>
              The Work is free from any encumbrances or any limitation of intellectual property rights;
            </li>
            <li>
              When the Work contains unoriginal content, the Creator has permission to use and incorporate the
              unoriginal content into its Work. The Creator represents and warrants that the sale, display, or
              performance of such Work via or on the Platform is not a violation of any agreement, contract, or
              obligation owed to any third-party.
            </li>
            <li>
              The Work does not incorporate any Prohibited Content (as defined below).
            </li>
          </ol>
        </li>
        <li>
          The initial Creator and the each Collector shall inform the further Collectors of any additional conditions
          for re-sale of the Digital Artwork, for example, royalty fee.
        </li>
        <h4>
          Purchasing the Work
        </h4>
        <li>
          The Collector can purchase the Digital Artwork by making bids on the auctions or by agreeing on and paying the
          instant purchase price (it means the price set by the Creator in its sole discretion allowing the Collector to
          purchase the Digital Artwork without bidding on the auction).
        </li>
        <h4>
          Purchasing the Work through an auction
        </h4>
        <li>
          When the Digital Artwork is listed for sale through an auction (reserved auction type), this auction is
          configured to automatically start once a bid is received equal or greater to a public reserved price set out
          by the Creator in its sole discretion. Once the reserved price is met, the auction is instantiated and
          configured to last for approximately 24-hours. Once initiated, the auction lasts 24 hours. If a bid is placed
          within the last 15 minutes of an auction, the auction time is extended by 15 minutes from the time of the bid
          in order to accommodate last minute bids.
        </li>
        <li>
          You (whether acting as a Creator or a Collector) hereby acknowledge and agree that auction timers used at the
          Platform may not be exact and the time calculated by such auction timers may not be accurate and represent
          only approximate value as to the start or end time remaining for the particular auction.
        </li>
        <li>
          You hereby acknowledge and agree that your bids are legally binding and irrevocable offers to purchase the
          Digital Artwork at the price of your bid and on conditions set out by the Creator in the Listing. By placing a
          bid you authorise a smart contract to lock your bid amount as well as to transfer your bid amount to the
          Creator if you win the auction.
        </li>
        <li>
          You hereby acknowledge and agree that the amount of your bid would be locked by a smart contract until the
          occurrence of one of the following events: i) you are outbid by the following prevailing bid, ii) you win the
          auction. In the event you are unbid, the smart contract would unlock and release your bid amount. In the event
          you win the auction, the smart contract would execute the transaction and transfer your bid amount from your
          wallet to the Creator’s wallet as well as transfer Digital Artwork to your wallet.
        </li>
        <li>
          The Creator may set an instant purchase price allowing the Collector to purchase the Digital Artwork without
          bidding on the auction. The Collector can purchase the Digital Artwork at instant purchase price by initiating
          purchase transaction through a smart contract and by authorising the smart contract to transfer the amount of
          instant purchase price from your wallet to the Creator’s wallet as well as transfer Digital Artwork to your
          wallet.
        </li>
      </ol>
      <h3>4. Secondary Market Sale</h3>
      <ol type='a'>
        <li>
          The initial Creator in the initial Listing for the Digital Artwork may set additional conditions for secondary
          market sale of this Digital Artwork, such as paying specific royalty fee at the rate set out by the initial
          Creator at its sole discretion. Each and any secondary market sale of this Digital Artwork then will be
          subject to this condition. The Creator shall inform the Collector of any additional conditions for re-sale of
          the Digital Artwork.
        </li>
        <li>
          In the event where the royalty fee for a secondary market sale of the Digital Artwork is set, the royalty fee
          shall be deducted from the purchase price of the secondary market sale and transferred to the initial Creator.
          When the secondary market sale is performed through the Platform, the smart contract would deduct a royalty
          fee and transfer it to the initial Creator’s wallet. When the secondary market sale is not performed through
          the Platform, the Creator shall transfer a royalty fee to the initial Creator’s wallet. You hereby acknowledge
          and agree that in case, you fail to transfer a royalty fee, this failure can be considered a breach of
          conditions of purchase of the Digital Artwork, and the initial Creator can claim void your secondary market
          sale.
        </li>
      </ol>
      <h3>5. Fees and Payments</h3>
      <ol type='a'>
        <li>
          In consideration for your use of the Platform and our services, interacting with related smart contracts,
          placing bids, creating Listings, making offers, purchasing Digital Artworks, issuing NFT tokens, charging
          royalty fee on your behalf, or making other transactions through the Platform, we may take fees and
          commissions. We will notify you of our fees and commissions prior you make any transaction that is subject to
          a fee and/or commission.
        </li>
        <li>
          We may apply service fee to your bids, offers, purchase price, royalty fee. We may charge a commission from a
          purchase price, instant purchase price, or any other value of purchase transactions.
        </li>
        <li>
          You hereby acknowledge and agree that all fees, commissions, and royalty fees are transferred, processed, or
          initiated directly through smart contracts on the corresponding blockchain (for example, Ethereum blockchain).
          By transacting through the Platform and by using smart contracts, you hereby acknowledge and agree to all
          automated fees, commissions, and royalty fees accrued due to your transactions made through the Platform. You
          agree to be bound by smart contracts’ execution and collection and distribution of all fees, commissions, and
          royalty fees.
        </li>
        <li>
          You hereby agree to pay all the applicable fees, commissions, and any other amounts incurred by you or on your
          behalf through the Platform, in the amounts that are in effect when such fees are incurred.
        </li>
        <li>
          Please be aware that any transactions made through the Platform are performed by related smart contract
          operating on the blockchain (for example, Ethereum blockchain) and are subjected to transaction fees of such
          blockchain (in Ethereum such transaction fee is called “Gas”). The amount of transaction fee is determined by
          the corresponding blockchain according to various factors, each of them is out of our control. It is your
          responsibility to be aware of applicable transaction fees and to pay such transaction fees. Prior you create
          the Listing, bid, or make a purchase, you shall check the applicable transaction fee. You hereby acknowledge
          and agree that under no circumstances will any contract, agreement, offer, sale, bid, or other transaction on
          the Platform be invalidated, revocable, retractable, or otherwise unenforceable on the basis that the
          transaction fee for the given transaction was unknown, too high, or otherwise unacceptable to you.
        </li>
        <li>
          It is your responsibility to determine whether, and to what extent, any taxes apply to any transactions you
          conduct through the Platform, and to withhold, collect, report and remit the correct amounts of taxes to the
          appropriate tax authorities. Your transaction history is available on the corresponding blockchain and may be
          examined via any decentralised application, wallet, gateway supporting such blockchain, or via the Platform.
          We do not undertake any responsibility to store or archive any blockchain-based transaction information, nor
          does the Platform have any such storage or archival capability.
        </li>
        <li>
          The Creator expressly agrees to refund to the Collector and/or us the purchase price amount received from the
          sale of a Digital Artwork that was subsequently removed from the Platform pursuant to an effective copyright
          infringement notice to which the Creator failed to timely submit an effective copyright infringement
          counter-notification. We, under any circumstance, will not be held liable to you for removing allegedly
          infringing Works from the Platform or otherwise fulfilling its legal obligations under UK copyright law
          (CDPA).
        </li>
      </ol>
      <h3>
        6. Intellectual Property Rights
      </h3>
      <ol type='a'>
        <li>
          Purchasing and collecting digital art and Digital Artworks is different from purchasing physical items or
          physical artworks. When you purchase the Digital Artwork, first of all, you purchase a related NFT token and
          get full rights in this NFT token, however, you will not get all the rights in the Related Work itself and you
          would not own the Related Work, rather you would get a limited license (as specified below) in this Related
          Work. You may show off your ownership of the Digital Artwork by displaying and sharing the Related Work, but
          you do not and would not have any legal ownership, right, or title to any copyrights, trademarks, or other
          intellectual property rights to the Related Work, excepting the limited license granted by these Terms.
        </li>
        <li>
          The Creator will assign to you all the rights in the Digital Artwork (NFT token) and you would become an owner
          of the Digital Artwork as soon as the sale transaction completes successfully. At the same time, the Creator
          will remain the owner of the Related Work and will own all rights, title and interest in the Related Work,
          including, but not limited to, copyright, but with restrictions set out below. As the copyright owner, the
          Creator has several exclusive rights to the Related Work, including the right to reproduce, the right to
          prepare derivative works, the right to distribute, and the right to display or perform the Related Work.
        </li>
        <li>
          Upon issuing the Digital Artwork, the Creator represent and warrant that the Creator did not issue and did not
          cause any third party to issue any NFT token or any other digital asset representing the same Related Work and
          any interest, title to the same Related Work, as well as the Creator irrevocably agrees not to issue in future
          any NFT token or any other digital asset representing the same Related Work, not to tokenise the same Related
          Work by other means whether currently known or later discovered.
        </li>
        <li>
          Upon issuing the Digital Artwork, the Creator hereby irrevocably acknowledges and agrees that it grants
          limited license in the Related Work to any owner of the Digital Artwork.
        </li>
        <li>
          By issuing the Digital Artwork via the Platform, the Creator affirmatively grants a limited, worldwide,
          non-assignable, non-sublicensable, royalty-free license to the Collector legally owning and properly obtained
          the Digital Artwork to display the Related Work. The Collector’s limited license to display the Related Work
          (including its graphic artwork cover), or perform the Related Work, includes, but is not limited to, the right
          to display or perform the Related Work privately or publicly: (i) for the purpose of promoting or sharing the
          Collector’s purchase, ownership, or interest, (ii) for the purpose of sharing, promoting, discussing, or
          commenting on the Digital Artwork; (iii) on third party marketplaces, exchanges, platforms, or applications in
          association with an offer to sell, or trade, the Digital Artwork; and (iv) within decentralised virtual
          environments, virtual worlds, virtual galleries, virtual museums, or other navigable and perceivable virtual
          environments, including simultaneous display of multiple copies of the Digital Artwork within one or more
          virtual environments.
        </li>
        <li>
          The Collector has the right to sell, trade, transfer, or use the Digital Artwork, but it may not make
          “commercial use” of the Related Work, for example, by selling copies of the Related Work, selling access to
          the Related Work, selling derivative works embodying the Related Work, or otherwise commercially exploiting
          the Related Work.
        </li>
        <li>
          The Collector agrees that it may not, nor permit any third party, to do or attempt to do any of the foregoing
          without the Creator’s express prior written consent in each case: (i) modify, distort, mutilate, or perform
          any other modification to the Related Work which would be prejudicial to the Creator’s honor or reputation;
          (ii) use the Related Work to advertise, market, or sell any third party product or service; (iii) use the
          Related Work in connection with images, videos, or other forms of media that depict hatred, intolerance,
          violence, cruelty, or anything else that could reasonably be found to constitute hate speech or otherwise
          infringe upon the rights of others; (iv) incorporate the Related Work in movies, videos, video games, or any
          other forms of media for a commercial purpose, except to the limited extent that such use is expressly
          permitted by these Terms or solely for Collector’s personal, non-commercial use; (v) sell, distribute for
          commercial gain, or otherwise commercialise merchandise that includes, contains, or consists of the Related
          Work; (vi) attempt to trademark, copyright, or otherwise acquire additional intellectual property rights in or
          to the Related Work; (vii) attempt to mint, tokenise, or create an additional cryptographic token representing
          the same Related Work, whether on or off of the Platform; (viii) falsify, misrepresent, or conceal the
          authorship of the Related Work; or (ix) otherwise utilise the Related Work for the Collector’s or any third
          party’s commercial benefit.
        </li>
        <li>
          By issuing the Digital Artwork via the Platform, the Creator affirmatively grants to us, our affiliates and
          successors a non-exclusive, worldwide, assignable, sublicensable, perpetual, and royalty-free license to make
          copies of, display, perform, reproduce, and distribute the Related Work on any media whether now known or
          later discovered for the broad purpose of operating, promoting, sharing, developing, marketing, and
          advertising the Platform, or any other purpose related to us, including without limitation, the express right
          to: (i) display or perform the Related Work on the Platform, a third party platform, social media posts,
          blogs, editorials, advertising, market reports, virtual galleries, museums, virtual environments, editorials,
          or to the public; (ii) create and distribute digital or physical derivative works based on the Related Work,
          including without limitation, compilations, collective works, and anthologies; (iii) indexing the Related Work
          in electronic databases, indexes, catalogues, smart contracts or distributed ledgers; and (iv) hosting,
          storing, distributing, and reproducing one or more copies of the Related Work within a distributed file
          keeping system, node cluster, or other database (for example, IPFS) or causing, directing, or soliciting
          others to do so.
        </li>
        <li>
          We reserve the right to remove any your content, Work or Digital Artwork that allegedly infringes another
          person’s copyright or trademark rights, thereby restricting access to or visibility of the Work on the
          Platform and restricting the owner’s ability to sell, access, or view the Work on the Platform. All
          transactions involving Digital Artworks are conducted with the knowledge and assumption of the risk that the
          Digital Artwork may subsequently be removed from the Platform as a consequence of a copyright dispute or a
          user’s violation of these Terms. We shall not be liable to you of a Digital Artwork that was subsequently
          taken down by us pursuant to a valid copyright infringement notice or a determination of a user’s violation of
          these Terms.
        </li>
      </ol>
      <h3>
        7. Intellectual Property Rights in the Platform
      </h3>
      <ol type='a'>
        <li>
          The Platform, including any enhancements, derivatives and improvements thereof, websites and mobile
          applications in order to access the Platform are our sole property. All the text, images, sounds, music,
          marks, logos, compilations (meaning the collection, arrangement and assembly of information), and other
          content that forms a part of the Platform (collectively, the “Content”) are the sole property of us, our
          affiliates or Creator that created Listings.
        </li>
        <li>
          We hereby grant you a limited, revocable, non-exclusive, non-transferable license, subject to the terms and
          conditions of these Terms, to access and use the Platform, and Content solely for purposes approved by us from
          time to time. Any other use of the Platform or Content is expressly prohibited and all other right, title, and
          interest in the Platform, website(s), applications or Content is exclusively the property of us and our
          affiliates, licensors and Creators.
        </li>
        <li>
          Nothing in these Terms shall grant you any of such property rights and/or ownership rights and shall not be as
          an assignment of such rights. Other than access to our services in accordance with the Terms, we do not in any
          way grant our users any license or other intellectual property rights. You shall not (nor attempt to) copy,
          decompile, reverse engineer, disassemble, derive the source code of, modify, or create derivative works of the
          underlying platforms of the Platform or any other part of the Content or in any other way manipulate the
          Platform.
        </li>
        <li>
          You shall not:
          <ol type='i'>
            <li>
              make copies of or further distribute the Platform, including copying onto any other medium;
            </li>
            <li>
              distribute, rent, sublicense, lease, resell, or assign the Platform;
            </li>
            <li>
              alter, modify or adapt the Platform, including but not limited to, translating, decompiling,
              disassembling, reverse engineering, or creating derivative works;
            </li>
            <li>
              resell, rent or otherwise provide access to the Platform to any third party;
            </li>
            <li>
              take any action in an attempt to obtain any other user’s data, cause malfunction, crash, tamper with or
              otherwise impair the Platform and related applications, and our related services; or
            </li>
            <li>
              use or access the Platform and related applications on platforms modified to circumvent the protections
              offered by the operating system.
            </li>
          </ol>
        </li>
        <li>
          You hereby grant us a non-exclusive, royalty-free right and license to use and display your trademarks, logos
          and photo images of you, if any, to mention your name and your testimonials in our promotional materials and
          advertisements. However, we obtain no rights in your trademarks and logos except for the limited right
          described herein, and you retain all right, title and interest in and to your trademarks and logos.
        </li>
      </ol>
      <h3>
        8. Restricted Content and Prohibited Activities
      </h3>
      <ol type='a'>
        <li>
          The Platform provides you with the functionality to upload your content, including, but not limited to, texts,
          images, sounds, photos, videos, messages and communications, profile information and any other materials and
          information (collectively, “User’s Content”).
        </li>
        <li>
          You hereby represent and warrant that you will not upload or anyhow provide User’s Content or otherwise post,
          transmit, distribute, or disseminate through the Platform any materials that (“Prohibited Content”):
          <ol type='i'>
            <li>
              are false, misleading, unlawful, obscene, indecent, lewd, pornographic, defamatory, libellous,
              threatening, harassing, hateful, abusive, or inflammatory;
            </li>
            <li>
              are patently offensive to the online community, such as that promotes racism, bigotry, hatred or physical
              harm of any kind against any group or individual; involve the transmission of “junk mail”, “chain
              letters”, or unsolicited mass mailing or “spamming”;
            </li>
            <li>
              encourage conduct that would be considered a criminal offense or gives rise to civil liability;
            </li>
            <li>
              promote an illegal or unauthorised copy of another person’s copyrighted work, such as providing pirated
              computer programs or links to them, providing information to circumvent manufacture installed copyright
              protected devices, or providing pirated music or links to pirated music files;
            </li>
            <li>
              provide instructional information about illegal activities such as making or buying illegal weapons,
              violating someone's privacy, or providing or creating computer viruses;
            </li>
            <li>
              breach or infringe any duty toward or rights of any person or entity, including rights of publicity or
              privacy;
            </li>
            <li>
              contain corrupted data or any other harmful, disruptive, or destructive files; or
            </li>
            <li>
              in our sole judgment, are objectionable, restrict or inhibit any person or entity from using or enjoying
              any portion of the Platform, or which may expose us, our affiliates or our customers to harm or liability
              of any nature.
            </li>
          </ol>
        </li>
        <li>
          You shall not:
          <ol type='i'>
            <li>
              manipulate the price of any Digital Artwork in any way, including bidding on your own items, preventing
              bidding, or using us to conceal economic activity;
            </li>
            <li>
              display or promote any pornographic, defamatory, libellous, threatening, harassing, hateful, abusive, or
              inflammatory content;
            </li>
            <li>
              display or promote physical harm of any kind against any group or individual, any instructional
              information about illegal activities such as making or buying illegal weapons, violating someone's
              privacy, or creating computer viruses;
            </li>
            <li>
              promote any information that you know is defamatory, false, misleading or promotes illegal activities or
              conduct that is abusive, threatening, obscene, defamatory or libellous;
            </li>
            <li>
              send junk mail or spam to our other users, including without limitation unsolicited advertising,
              promotional materials, or other solicitation material;
            </li>
            <li>
              harvest or collect email addresses or other contact information of our other users;
            </li>
            <li>
              defame, harass, abuse, threaten, or defraud our users, or collect or attempt to collect, personal
              information about users or third parties without their consent;
            </li>
            <li>
              use the Platform for any illegal purpose, or in violation of any local, state, national, or international
              law or regulation, including without limitation laws governing intellectual property and other proprietary
              rights, data protection, and privacy;
            </li>
            <li>
              access or use the Platform for the purpose of creating a product or service that is competitive with any
              of our products or services.
            </li>
          </ol>
        </li>
        <li>
          You shall be fully liable and responsible for your Content and actions made at or via the Platform. You shall
          indemnify and hold us harmless in all and any claims and issues as it set out in these Terms.
        </li>
      </ol>
      <h3>
        9. Risks of Dealing with Crypto Assets
      </h3>
      <ol type='a'>
        <li>
          Holding and purchasing crypto assets, including NFT tokens, interacting with decentralised protocols, smart
          contracts, non-custodial wallets (like MetaMask), distributed ledgers and blockchains involves significant
          risks and potential for financial losses, including, without limitation, the following:
          <ol type='i'>
            <li>
              the features, functions, characteristics, operation, use and other properties of any crypto assets (“Asset
              Properties”) and the software, smart contracts, networks, protocols, systems, and other technology
              (including, if applicable, any distributed ledger (blockchain)) (“Underlying Technology”) used to
              administer, create, issue, transfer, cancel, use or transact in crypto assets may be complex, technical or
              difficult to understand or evaluate;
            </li>
            <li>
              any crypto asset and its Underlying Technology may be vulnerable to attacks on the security, integrity or
              operation of the crypto asset or its Underlying Technology (“Attacks”), including Attacks using computing
              power sufficient to overwhelm the normal operation of a decentralised distributed ledger (blockchain) or
              other Underlying Technology;
            </li>
            <li>
              any smart contract, decentralised protocol, distributed ledger may be vulnerable to Attacks, including
              phishing attacks. Any smart contract or decentralised protocol may cease to operate as expected due to
              various reasons, including Attacks, enforcement and regulatory activities, scamming activities, technical
              and communication issues. We do not make any representation and warranty that these smart contracts or
              decentralised protocols are safe, secure, verified or verifiable, or of any value or quality or legality;
            </li>
            <li>
              any crypto asset, Asset Properties or Underlying Technology may change or otherwise cease to operate as
              expected due to a change made to the Underlying Technology, a change made using features or functions
              built into the Underlying Technology or a change resulting from an Attack. These changes may include,
              without limitation, a “fork” or “rollback” of the crypto asset or blockchain;
            </li>
            <li>
              any crypto asset may decrease in value or lose all of its value due to various factors including discovery
              of wrongful conduct, market manipulation, changes to Asset Properties or perceived value of Asset
              Properties, Attacks, suspension or cessation of support for a crypto asset by decentralised protocols,
              trading platforms, marketplace platforms or service providers, and other factors outside our control;
            </li>
            <li>
              any crypto asset may decrease in value or lose all of its value due to legislative or regulatory activity,
              or other government action;
            </li>
            <li>
              we undertake no responsibility for conducting any due diligence or screening process with regard to any
              Digital Artwork that is accessible or discoverable through the Platform;
            </li>
            <li>
              we may at any time be required by governmental authorities to freeze accounts or provide information about
              users.
            </li>
          </ol>
        </li>
        <li>
          The risks described in this Section may result in loss of crypto assets, decrease in or loss of all value or
          exchangeability of crypto assets, inability to access or transfer crypto assets, inability to exchange crypto
          assets, inability to receive financial benefits available to other crypto assets holders, and other financial
          losses to you. You hereby assume and agree that we will have no responsibility or liability for, such risks.
          You hereby irrevocably waive, release and discharge any and all claims, whether known or unknown to you,
          against us, our affiliates and their respective shareholders, members, directors, officers, employees, agents
          and representatives related to any of the risks set forth herein.
        </li>
        <li>
          You represent and warrant that you have: (a) the necessary technical expertise and ability to review and
          evaluate the security, integrity and operation of any crypto assets that you decide to acquire; and (b) the
          knowledge, experience, understanding, professional advice and information to make your own evaluation of the
          merits and risks of any crypto assets. You accept the risk of acquiring any crypto assets, including Digital
          Artworks, via the Platform or via any other decentralized marketplace platform and are solely responsible for
          conducting your own independent analysis of the risks specific to any crypto assets, including Digital
          Artworks, the Platform or the corresponding blockchain (like Ethereum blockchain). You acknowledge that in
          purchasing or exchanging any crypto assets, including Digital Artworks, you are not relying in any manner on
          us or our affiliates in making such purchase or exchange, and the fact that any particular Digital Artwork, is
          accessible or discoverable through the Platform in no way constitutes any endorsement or indication that
          Digital Artwork has undergone any form of due diligence review or qualification, and in no way indicates any
          party’s opinion that the Digital Artwork is safe, suitable, true to any third party representations made,
          verified or verifiable, or of any value or quality or legality.
        </li>
        <li>
          Whether any information on any crypto assets is accessible or discoverable via the Platform does not indicate
          our approval or disapproval of the crypto asset, the Digital Artwork or the integrity, security or operation
          of the crypto asset or its Underlying Technology.
        </li>
        <li>
          We do not provide any advice, do not have any fiduciary duty to you or any other user and do not make any
          warranty about the suitability of any crypto assets for ownership by you.
        </li>
      </ol>
      <h3>
        10. Warranties
      </h3>
      <ol type='a'>
        <li>
          EXCEPT FOR THE EXPRESS WARRANTIES SET FORTH IN THESE TERMS, WE HEREBY DISCLAIM ALL EXPRESS OR IMPLIED
          WARRANTIES WITH REGARD TO THE PLATFORM, SMART CONTRACTS OR OUR SERVICES, INCLUDING, BUT NOT LIMITED TO, ANY
          IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT AND QUALITY.
          WE MAKE NO REPRESENTATIONS OR WARRANTIES REGARDING THE RELIABILITY, AVAILABILITY, TIMELINESS, SUITABILITY,
          ACCURACY OR COMPLETENESS OF THE PLATFORM, SMART CONTRACTS, OUR SERVICES OR THE RESULTS YOU MAY OBTAIN BY USING
          THE PLATFORM. WITHOUT LIMITING THE GENERALITY OF THE FOREGOING, WE DO NOT REPRESENT OR WARRANT THAT: (I) THE
          OPERATION OR USE OF THE PLATFORM OR SMART CONTRACTS WILL BE TIMELY, UNINTERRUPTED OR ERROR-FREE; OR (II) THE
          QUALITY OF THE PLATFORM OR SMART CONTRACTS WILL MEET YOUR REQUIREMENTS.
        </li>
        <li>
          YOU ACKNOWLEDGE AND AGREE THAT WE DO NOT CONTROL THE TRANSFER OF DATA OVER COMMUNICATIONS FACILITIES,
          INCLUDING THE INTERNET AND BLOCKCHAINS, AND THAT THE PLATFORM OR SMART CONTRACTS MAY BE SUBJECT TO
          LIMITATIONS, DELAYS, AND OTHER PROBLEMS INHERENT IN THE USE OF SUCH COMMUNICATIONS FACILITIES. WE ARE NOT AND
          SHALL NOT BE RESPONSIBLE FOR ANY DELAYS, DELIVERY FAILURES, OR OTHER DAMAGE RESULTING FROM SUCH PROBLEMS.
          WITHOUT LIMITING THE FOREGOING, WE DO NOT WARRANT OR GUARANTEE THAT ANY OR ALL SECURITY ATTACKS WILL BE
          DISCOVERED, REPORTED OR REMEDIED, OR THAT THERE WILL NOT BE ANY SECURITY BREACHES BY THIRD PARTIES. EXCEPT
          WHERE EXPRESSLY PROVIDED OTHERWISE BY US, THE PLATFORM AND SMART CONTRACTS ARE PROVIDED TO YOU ON AN "AS IS"
          BASIS.
        </li>
        <li>
          YOU ACCEPT RESPONSIBILITY OF ALL ACTIVITIES AND CONTENT GENERATED BY YOU VIA THE PLATFORM.
        </li>
      </ol>
      <h3>
        11. Limitation of Liability
      </h3>
      <ol type='a'>
        <li>
          IN NO EVENT WE WILL BE LIABLE UNDER ANY CONTRACT, NEGLIGENCE, STRICT LIABILITY OR OTHER THEORY: (I) FOR ANY
          INDIRECT, EXEMPLARY, INCIDENTAL, SPECIAL OR CONSEQUENTIAL DAMAGES; (II) FOR LOSS OF USE, INACCURACY, COST OF
          PROCUREMENT OF SUBSTITUTE GOODS, SERVICES OR TECHNOLOGY, LOSS OF PROFITS, DATA OR BUSINESS INTERRUPTION; OR
          (III) FOR ANY MATTER BEYOND OUR REASONABLE CONTROL, WHETHER OR NOT FORESEEABLE, EVEN IF WE HAVE BEEN ADVISED
          OF THE POSSIBILITY OF SUCH LOSS OR DAMAGE, AND EVEN IF A REMEDY SET FORTH HEREIN HAS FAILED ITS ESSENTIAL
          PURPOSE.
        </li>
        <li>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW AND RULES, OUR AGGREGATE LIABILITY FOR ALL CLAIMS UNDER
          THESE TERMS SHALL BE LIMITED TO THE TOTAL FEES ACTUALLY PAID BY YOU TO US IN THE TWELVE (12) MONTHS
          IMMEDIATELY PRECEDING THE DATE OF THE APPLICABLE CLAIM.
        </li>
        <li>
          WE WILL NOT BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL
          OR EXEMPLARY DAMAGES (INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL USE, OR DATA), EVEN
          IF A PARTY HAS BEEN ON NOTICE OF THE POSSIBILITY OF SUCH DAMAGES. FURTHER, WE WILL NOT BE RESPONSIBLE FOR ANY
          COMPENSATION OR DAMAGES ARISING IN CONNECTION WITH: (A) YOUR INABILITY TO USE THE PLATFORM, INCLUDING AS A
          RESULT OF ANY (I) DENIAL OF ACCESS TO OR SUSPENSION OF ANY CONNECTED WALLET PURSUANT TO THESE TERMS, (II) OUR
          DISCONTINUATION OF ANY OR ALL PARTS OF THE PLATFORM, OR, (III) ANY UNANTICIPATED OR UNSCHEDULED DOWNTIME OF
          ALL OR A PORTION OF THE PLATFORM OR SMART CONTRACTS FOR ANY REASON, INCLUDING AS A RESULT OF POWER OUTAGES,
          SYSTEM FAILURES OR OTHER INTERRUPTIONS; (B) ANY INVESTMENTS, EXPENDITURES, OR COMMITMENTS MADE BY YOU OR YOUR
          USE OF OR ACCESS TO THE PLATFORM OR SMART CONTRACTS; OR (D) ANY UNAUTHORISED ACCESS TO, ALTERATION OF, OR THE
          DELETION, DESTRUCTION, DAMAGE, LOSS OR FAILURE TO STORE ANY OF CONNECTED WALLET OR OTHER DATA.
        </li>
        <li>
          YOU ACCEPT RESPONSIBILITIES OF ALL ACTIVITIES AND CONTENTS GENERATED BY YOU ON THE PLATFORM. YOU FURTHER
          ACKNOWLEDGE THAT WE DO NOT ACT AS YOUR BROKER-DEALER, INTERMEDIARY, AGENT OR ADVISOR AND OWES YOU NO FIDUCIARY
          DUTY. ANY COMMUNICATION BY US TO YOU SHALL NOT BE CONSTRUED UNDER ANY CIRCUMSTANCES AS LEGAL, TAX, ACCOUNTING
          OR FINANCIAL ADVICE. ANY USE OR ACCESS TO THE PLATFORM SHALL NOT BE CONSTRUED AS THE FACILITATION OF ANY SALE
          OR EXCHANGE OF SECURITIES AS AN EXCHANGE.
        </li>
      </ol>
      <h3>
        12. Indemnification
      </h3>
      <ol type='a'>
        <li>
          You agree to defend, indemnify and hold us harmless as well as our affiliates and their respective
          shareholders, members, directors, officers, employees, agents and representatives from and against any and all
          claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to
          attorney’s fees) arising out of or related to: (i) your use and access to the Platform, smart contracts and
          our services, including your transactions made through the Platform, any data or content transmitted or
          received by you; (ii) your violation of any term of these Terms, including without limitation your breach of
          any of the representations and warranties above; (iii) your violation of any third-party right, including
          without limitation any right of privacy, publicity rights or intellectual property rights; (iv) your violation
          of any applicable law, rule or regulation; (v) any actual or alleged breach of your representations,
          warranties, or obligations set forth in these Terms; (vi) negligent or willful misconduct; or (vii) any other
          party’s access and use of other appropriate security code. The foregoing shall include the actions of any
          third party who wrongfully commit these actions under your account, accessing through your wallet or utilizing
          your password and/or private keys.
        </li>
      </ol>
      <h3>
        13. Use and Termination.
      </h3>
      <ol type='a'>
        <li>
          We may: (a) suspend, restrict, or terminate your access to any or all of our services to you as a user if: (i)
          we are so required by a facially valid subpoena, court order, or binding order of a government authority; (ii)
          we reasonably suspect you of using your connected wallet in connection with a prohibited use or activity;
          (iii) if there is any pending litigation, investigation, or government proceeding relating to your connected
          wallet and/or we perceive a heightened risk of legal or regulatory non-compliance associated with your
          connected wallet activity; (iv) if we or our service partners are unable to support your use; (v) if you take
          any action that we consider to be circumventing our controls, including, but not limited to, connecting
          multiple wallets or abusing promotions which we may offer from time to time; (vi) if you, your content, your
          Works or your Digital Artworks infringes intellectual property, rights, patents, copyrights or trademarks of
          any third parties, or includes any Prohibited Content; or (vii) if you breach of term or condition of these
          Terms.
        </li>
        <li>
          If we terminate your access to the Platform or your use of our services for any reason, we will provide you
          with notice of our actions, if it would be technically possible and if other is not set in these Terms, and
          unless a court order or other legal process prohibits us from providing you with such notice. You acknowledge
          that our decision to take certain actions, including limiting access to our services may be based on
          confidential criteria that are essential to security protocols. You agree that we are under no obligation to
          disclose the details of our security procedures to you.
        </li>
      </ol>
      <h3>
        14. Notices and Communications
      </h3>
      <ol type='a'>
        <li>
          If you have a dispute with us (a “Complaint”), you agree to contact us through our support team via
          email: <ExternalLink href={SUPPORT_EMAIL}>{SUPPORT_EMAIL}</ExternalLink> to attempt to resolve any such
          dispute amicably within a period of 30 days. If you do not follow this procedure set out before filing a legal
          claim or suit, we shall have the right to ask the court or arbitration to dismiss your filing unless and until
          you complete the following step.
        </li>
        <li>
          We operate a newsfeed in order to provide information to our users concerning new developments and other
          pertinent notifications (the “Newsfeed”). Through the Newsfeed all users of the Platform receive the same
          information at the same time. Due to the fact that we do not necessarily have the mailing address and e-mail
          address of every user, the Newsfeed serves as the primary means of communication with our users. By using the
          Platform, you agree that any notices or communications concerning the Platform or your connected wallet shall
          be made to you through the Newsfeed, and all of our communications that are intended to be notices under these
          terms shall be deemed valid notice, as of the date and time of posting, if delivered by us by making it
          accessible by your account, via the Newsfeed. You further agree and acknowledge that we provide postings on a
          commercially reasonable basis and for the convenience of users, and except as expressly provided under these
          Terms we assume no liability or responsibility for the completeness, accuracy or timeliness of the information
          posted on the Newsfeed; none of the information posted by us on the Newsfeed constitutes investment advice.
        </li>
        <li>
          All notices and communications by you to us under these Terms shall be made in writing to our customer support
          team or by email: <ExternalLink href={SUPPORT_EMAIL}>{SUPPORT_EMAIL}</ExternalLink> and are effective on the
          date received (unless the notice specifies a later date).
        </li>
      </ol>
      <h3>
        15. General Terms
      </h3>
      <ol type='a'>
        <li>
          From time to time, we may make available special offers or conduct promotions to certain customers. Subject to
          applicable laws, we or the issuer of crypto assets subject to an offer or promotion may establish qualifying
          criteria to participate in any special promotion in our sole discretion. We may revoke any special offer at
          any time without notice. We shall have no obligation to make special offers available to all customers. We
          make no recommendation and do not provide any advice about the value or utility of any crypto asset subject to
          a promotion.
        </li>
        <li>
          We are an independent contractor for all purposes. Nothing in these Terms shall be deemed or is intended to be
          deemed, nor shall it cause, you and us to be treated as partners, joint venturers, or otherwise as joint
          associates for profit, or either you or us to be treated as the agent of the other.
        </li>
        <li>
          We shall not be liable for any damage or interruptions caused by any computer viruses, spyware, scareware,
          Trojan horses, worms or other malware that may affect your computer or other equipment, or any phishing,
          spoofing or other attack. We advise the regular use of a reputable and readily available virus screening and
          prevention software. You should also be aware that email services are vulnerable to spoofing and phishing
          attacks and should use care in reviewing messages purporting to originate from us.
        </li>
        <li>
          We may modify these Terms by providing notice of such changes, such as by sending you an email, providing
          notice through the Platform, or updating the “Last Updated” date at the bottom of these Terms. By continuing
          to access or use of the Platform, you confirm your agreement to the modified Terms. If you do not agree to any
          modification to these Terms, you must stop using the Platform and our services. We encourage you to frequently
          review the Terms to ensure you understand the terms and conditions that apply to your access to, and use of,
          the Platform.
        </li>
        <li>
          These Terms of Service, the Privacy Policy
          {' '}
          <Link href={PRIVACY_POLICY}>
            https://triptcip.com/privacy
          </Link>
          , and all other documents incorporated by
          reference herein and therein, comprise the entire agreement between you and us.
        </li>
        <li>
          Section headings in these Terms are for convenience only, and shall not govern the meaning or interpretation
          of any provision of these Terms.
        </li>
        <li>
          These Terms are personal to you, you cannot transfer or assign your rights, licenses, interests and/or
          obligations to anyone else. We may transfer or assign our rights, licenses, interests and / or our obligations
          at any time, including as part of a merger, acquisition or other corporate reorganization involving the
          Platform.
        </li>
        <li>
          If any provision of these Terms is determined to be invalid or unenforceable under any applicable law, this
          will not affect the validity of any other provision.
        </li>
        <li>
          We may not always strictly enforce our rights under these Terms. If we do this, it will be just a temporary
          measure and we may enforce our rights strictly again at any time.
        </li>
        <li>
          These Terms and any information or notifications that you or we are to provide should be in English. Any
          translation of these Terms or other documents is provided for your convenience only. In the event of any
          inconsistency, the English language version of these Terms or other documents shall prevail.
        </li>
        <li>
          Upon termination of these Terms, all sections of these Terms which by their nature should survive termination
          or expiration will survive, including without limitation, the following sections: Section 2(g), Section 2(h),
          Section 4, Section 5(c), Section 5(e), Section 5(g), Section 6, Section 7, Section 8, Section 9, Section 10,
          Section 11, Section 12, Section 16 and this Section 15(k).
        </li>
        <li>
          READ THIS SECTION CAREFULLY BECAUSE IT REQUIRES THE PARTIES TO SUBMIT THEIR DISPUTES TO BINDING ARBITRATION
          AND LIMITS THE MANNER IN WHICH YOU CAN SEEK RELIEF FROM US.
          <br />
          <br />
          These Terms are deemed entered into by the parties in the United Kingdom. These Terms shall be governed
          exclusively under the laws of England and Wales. Any dispute arising out of or in connection with this Terms,
          including any question regarding its existence, validity or termination, shall be referred to and finally
          resolved by arbitration under the LCIA Rules, which Rules are deemed to be incorporated by reference into this
          clause. The number of arbitrators shall be one. The seat, or legal place, of arbitration shall be London, UK.
          The language to be used in the arbitral proceedings shall be English. The LCIA's Rules are available
          at
          {' '}
          <ExternalLink href='https://lcia.org/Dispute_Resolution_Services/lcia-arbitration-rules-2014'>
            https://lcia.org/Dispute_Resolution_Services/lcia-arbitration-rules-2014
          </ExternalLink>.
          Payment of all filing, administration and arbitrator fees will be governed by the LCIA's rules.
        </li>
      </ol>
      <h3>
        16. Copyright infringements
      </h3>
      <ol type='a'>
        <li>
          It is our policy to respond to alleged infringement notices that comply with the UK Copyright, Designs and
          Patents Act 1988 (“CDPA”). If you believe that your copyrighted work has been copied in a way that constitutes
          copyright infringement and is accessible via the Platform, please notify us as set forth in the CDPA. For your
          complaint to be valid under the CDPA, you must provide the following information in writing:
          <ol type='i'>
            <li>
              An electronic or physical signature of a person authorised to act on behalf of the copyright owner;
            </li>
            <li>
              Identification of the copyrighted work that you claim has been infringed;
            </li>
            <li>
              Identification of the material that is claimed to be infringing and where it is located on the Platform;
            </li>
            <li>
              Information reasonably sufficient to permit us to contact you, such as your address, telephone number, and
              email address;
            </li>
            <li>
              A statement that you have a good faith belief that use of the material in the manner complained of is not
              authorised by the copyright owner, its agents, or law; and
            </li>
            <li>
              A statement, made under penalty of perjury, that the above information is accurate, and that you are the
              copyright owner or are authorized to act on behalf of the owner.
            </li>
          </ol>
        </li>
        <li>
          PLEASE NOTE THAT IF YOU KNOWINGLY MISREPRESENT THAT ONLINE MATERIAL IS INFRINGING, YOU MAY BE SUBJECT TO
          CRIMINAL PROSECUTION FOR PERJURY AND CIVIL PENALTIES, INCLUDING MONETARY DAMAGES, COURT COSTS, AND ATTORNEYS’
          FEES.
        </li>
        <li>
          We will notify a user provided the User’s Content that an infringement notification has been received and that
          the infringing User’s Content will be removed at the end of 24 hours unless a counter-notice is supplied to
          us. If no legitimate counter-notice is supplied, the infringing User’s Content will be removed from the
          Platform.
        </li>
        <li>
          If a counter-notice is received by our customer support, we will restore access to such Content and notify the
          original complaining party. The User’s Content will then remain published unless the copyright owner files an
          action seeking a court order against the user provided the User’s Content.
        </li>
        <li>
          Please contact our support team in order to file complaint at the following address:
          PITCCH LTD, Attn: Copyright Infringement
          Email – <ExternalLink href={SUPPORT_EMAIL}>{SUPPORT_EMAIL}</ExternalLink>
          {' '}
          Address – 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, UK
        </li>
      </ol>
      <br />
      Last Updated on 17 March 2021.
    </LegalPage>
  )
}

export default TermsView
